<template>
  <div class="md-layout">
    <div class="md-layout-item md-size-100">
      <md-card>
        <md-card-header class="md-card-header-icon md-card-header-blue">
          <div class="card-icon">
            <md-icon>event_available</md-icon>
          </div>
          <h4 class="title">
            Bookings
          </h4>
        </md-card-header>
        <md-card-content>
          <report-dashboard />

          <md-table
            id="booking-list"
            v-model="paginatedBookings.mdData"
            md-card
            @md-selected="onSelect"
          >
            <md-table-toolbar class="md-layout">
              <div class="md-toolbar-section-start">
                <md-field
                  md-clearable
                  class="md-layout-item md-size-60"
                  @md-clear="clearSearch"
                >
                  <md-icon>search</md-icon>
                  <md-input
                    placeholder="Search"
                    type="text"
                    v-model="search"
                    v-on:keyup.enter="() => updatePagination()"
                  />
                </md-field>
                <template class="md-layout-item md-size-10"></template>
                <md-field
                  class="md-layout-item md-size-20"
                  style="margin-left:2em;"
                >
                  <label for="adventure_type">Filter Adventure Type</label>
                  <md-select
                    v-on:md-selected="changeAdventureType"
                    v-model="booking_type"
                    name="adventure_type"
                    id="adventure_type"
                  >
                    <md-option value="all">All Bookings</md-option>
                    <md-option value="tours">Tour Bookings</md-option>
                    <md-option value="events">Event Bookings</md-option>
                  </md-select>
                </md-field>
              </div>
              <div class="md-toolbar-section-end">
                <md-button
                  class="md-icon-button md-dense md-primary md-round"
                  @click="addBooking"
                >
                  <md-icon>add</md-icon>
                  <md-tooltip md-direction="top"
                    >Create a new Booking</md-tooltip
                  >
                </md-button>
              </div>
            </md-table-toolbar>

            <md-table-row
              slot="md-table-row"
              slot-scope="{ item }"
              md-selectable="single"
            >
              <md-table-cell md-label="ID"
                ><router-link
                  :to="{ name: 'Booking Profile', params: { id: item.id } }"
                  >{{ item.id }}</router-link
                ></md-table-cell
              >
              <md-table-cell md-label="Date Booked">{{
                item.booking_date | formatDate
              }}</md-table-cell>
              <md-table-cell md-label="Customer">
                {{ item.lead_booker.full_name }}
              </md-table-cell>
              <md-table-cell md-label="Adventure">
                <i
                  v-if="item.departure.tour.type.value == 'event'"
                  class="far fa-calendar text-warning tour-type-icon"
                  ><md-tooltip md-direction="right"
                    >This is an Event Booking</md-tooltip
                  ></i
                >
                {{ item.departure.tour.title }}
              </md-table-cell>
              <md-table-cell md-label="Departure">
                {{ item.departure.start_date | formatDate }}
              </md-table-cell>
              <md-table-cell md-label="Manager">
                {{ item.manager.full_name }}
              </md-table-cell>
              <md-table-cell md-label="Amount" md-numeric
                >{{ item.financials.booking_total }}
                <small>{{ item.financials.currency }}</small></md-table-cell
              >
              <md-table-cell md-label="Balance" md-numeric
                >{{ item.financials.balance }}
                <small>{{ item.financials.currency }}</small></md-table-cell
              >
              <md-table-cell md-label="Status">
                <booking-status
                  :status-value="item.status.value"
                  :status-description="item.status.description"
                />
              </md-table-cell>
              <md-table-cell class="text-right">
                <md-button
                  class="md-danger md-just-icon md-simple"
                  @click.stop="deleteBooking(item)"
                >
                  <md-icon>delete</md-icon>
                  <md-tooltip md-direction="top">Delete Booking</md-tooltip>
                </md-button>
              </md-table-cell>
            </md-table-row>

            <md-table-empty-state
              md-label="Nothing to Show"
              :md-description="`We can't find any bookings to show.`"
            >
              <md-button class="md-primary md-raised" @click="addBooking"
                >Create a new Booking</md-button
              >
            </md-table-empty-state>

            <md-table-pagination
              :md-update="updatePagination"
              :md-data.sync="paginatedBookings"
              :md-total="paginatedBookings.mdCount"
              :md-page-options="false"
              :md-page-size="pageSize"
              md-label="Bookings Per Page"
            />
          </md-table>
        </md-card-content>
      </md-card>
    </div>

    <booking-create-dialog
      :showNewBookingDialog="showNewBookingDialog"
      @new-booking="newBooking"
      @cancel="showNewBookingDialog = false"
    />
  </div>
</template>

<script>
import bookingApi from "@/services/api/booking";
import _filter from "lodash/filter";
import Swal from "sweetalert2";
import { mapFields } from "vuex-map-fields";

import BookingCreateDialog from "./BookingCreateDialog.vue";
import ReportDashboard from "@/components/Handi/Reports/ReportDashboard";
import BookingStatus from "@/components/BookingStatus.vue";

export default {
  data() {
    return {
      search: null,
      booking_id: null,
      showNewBookingDialog: false,
      pageSize: 20,
      paginatedBookings: {
        mdCount: null,
        mdPage: 1,
        mdData: []
      }
    };
  },

  components: {
    BookingCreateDialog,
    ReportDashboard,
    BookingStatus
  },

  methods: {
    clearSearch() {
      this.search = null;
      this.updatePagination();
    },

    changeAdventureType() {
      this.updatePagination();
    },

    updatePagination(page, pageSize, sort, sortOrder) {
      bookingApi
        .getBookings({
          with_cancelled: true,
          search: this.search,
          booking_type: this.booking_type,
          page: page,
          per_page: pageSize
        })
        .then(response => {
          let bookingInfo = response.data;
          this.paginatedBookings = {
            mdCount: bookingInfo.count,
            mdPage: bookingInfo.currentPage,
            mdData: bookingInfo.bookings
          };
        });
    },

    newBooking(booking) {
      this.showNewBookingDialog = false;
      this.onSelect(booking);
    },

    addBooking() {
      this.showNewBookingDialog = true;
    },

    deleteBooking(booking) {
      Swal.fire({
        title: "Are you absolutely sure?",
        text: `This will delete the Booking, along with all related data!`,
        type: "warning",
        showCancelButton: true,
        confirmButtonClass: "md-button md-success",
        cancelButtonClass: "md-button md-danger",
        confirmButtonText: "Yes, delete it!",
        buttonsStyling: false
      }).then(result => {
        if (result.value) {
          bookingApi.deleteBooking(booking).then(response => {
            this.bookings = _filter(this.bookings, function(item) {
              return booking.id !== item.id;
            });

            this.$notify({
              timeout: 2500,
              message: "The Booking has been deleted.",
              icon: "cancel",
              horizontalAlign: "center",
              verticalAlign: "top",
              type: "danger"
            });
          });
        }
      });
    },

    onSelect(booking) {
      this.$router.push({
        name: "Booking Profile",
        params: { id: booking.id }
      });
    }
  },

  computed: {
    ...mapFields("bookingFilter", ["booking_type"])
  },

  watch: {},

  created() {}
};
</script>
<style lang="scss">
@import "@/assets/scss/md/_variables.scss";
.tour-type-icon {
  margin-right: 5px;
}
.md-table-head-label {
  padding-right: 0;
  font-size: 0.8rem;
}

#new-booking-dialog .md-dialog-container {
  max-width: 768px;
  min-width: 768px;
  width: 768px;
}

#booking-list {
  tr td:nth-child(6),
  tr th:nth-child(6) {
    width: 150px;
  }
  &::v-deep .md-table-head-label {
    padding-right: 0;
  }
}
</style>
